import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { registerCheatingAttempt } from "../../../redux/Actions/placementSecurityActions";
import { applyTestRestrictions } from "../../../utils/placementTestRestrictions";
import {
  fetchStudentPlacementBundle,
  submitPlacementAnswers,
} from "../../../redux/Actions/studentPlacementActions";
import {
  resetStudentAnswers,
  setMcqSelection,
} from "../../../redux/Actions/studentPlacementAnswerActions";
import Meta from "../../Meta/Meta";
import { shouldRequirePlacementFullscreen } from "../../../utils/placementFullscreen";

const PlacementTestMain = () => {
  const { t, i18n } = useTranslation("ui");
  const isArabic = i18n.language === "ar";
  const [tabIndex, setTabIndex] = useState(0);
  const [timeLeft, setTimeLeft] = useState(75 * 60);
  const [openModal, setOpenModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [confirmEndTest, setConfirmEndTest] = useState(false);
  const [guestCompleted, setGuestCompleted] = useState(false);
  const [studentCompleted, setStudentCompleted] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { bundle, bundleLoading, bundleError } = useSelector(
    (s) => s.studentPlacement || {}
  );
  const { mcq: selected } = useSelector(
    (s) => s.studentPlacementAnswers || { mcq: {} }
  );

  const isEndingRef = useRef(false);
  const lastSentRef = useRef(0);
  const timerRef = useRef(null);
  const requireFullscreenRef = useRef(shouldRequirePlacementFullscreen());

  useEffect(() => {
    const prevDir = document.documentElement.dir;
    const prevLang = document.documentElement.lang;
    document.documentElement.dir = "ltr";
    document.documentElement.lang = "en";
    return () => {
      document.documentElement.dir = prevDir;
      document.documentElement.lang = prevLang;
    };
  }, []);

  const exitFullscreenAndNavigate = useCallback(
    async (target = "/dashboard") => {
      try {
        if (document.fullscreenElement) {
          await document.exitFullscreen();
        }
      } catch (err) {
        console.warn("Could not exit fullscreen:", err);
      } finally {
        navigate(target);
      }
    },
    [navigate]
  );

  const endAndCleanup = async (message) => {
    if (isEndingRef.current) return;
    isEndingRef.current = true;

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    setModalMessage(`${message || t("placementSubmission.ended")}\n\n${t("placementSubmission.submitting")}`);
    setOpenModal(true);

    // Capture the attempt type before dispatch: a successful guest submission clears
    // transient state, but the completion UI still needs to know it was a guest attempt.
    const isGuest = !!localStorage.getItem("placementGuestToken");
    const res = await dispatch(submitPlacementAnswers());

    if (!res?.ok) {
      setModalMessage(t("placementSubmission.failed", { error: res?.error || t("placementSubmission.unknownError") }));
      // Keep answers and the guest token so the user can close the notice and retry.
      isEndingRef.current = false;
      return;
    } else {
      const emailSent = res?.data?.emailSent !== false;
      setModalMessage(t(
        isGuest
          ? emailSent ? "placementSubmission.guestSuccess" : "placementSubmission.guestEmailFailed"
          : emailSent ? "placementSubmission.studentSuccess" : "placementSubmission.studentEmailFailed"
      ));
    }

    dispatch(resetStudentAnswers());
    if (isGuest) {
      localStorage.removeItem("placementGuestToken");
      localStorage.removeItem("placementGuestStudentId");
      if (res?.ok) {
        setGuestCompleted(true);
        return;
      }
    }
    setStudentCompleted(true);
    setTimeout(
      () => exitFullscreenAndNavigate("/dashboard?tab=placementTestResult"),
      2500
    );
  };

  const recordCheatingAttempt = useCallback(
    async (reason) => {
      if (isEndingRef.current) return;

      const now = Date.now();
      if (now - lastSentRef.current < 800) return;
      lastSentRef.current = now;

      try {
        const result = await dispatch(registerCheatingAttempt());
        if (!result?.ok) {
          setModalMessage(result?.error || reason || "Failed to record cheating attempt.");
          setOpenModal(true);
          return;
        }
        const data = result.data;
        const status = (data?.status || "").toUpperCase();

        setModalMessage(data?.message || reason || "Notice");
        setOpenModal(true);

        if (status === "BLACKLISTED") {
          setTimeout(() => {
            dispatch(resetStudentAnswers());
            exitFullscreenAndNavigate();
          }, 2500);
        }
      } catch (err) {
        setModalMessage(err?.message || "Unexpected error while recording attempt.");
        setOpenModal(true);
      }
    },
    [dispatch, exitFullscreenAndNavigate]
  );

  useEffect(() => {
    dispatch(fetchStudentPlacementBundle());
  }, [dispatch]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, [tabIndex]);

  useEffect(() => {
    const prevDir = document.documentElement.dir;
    const prevLang = document.documentElement.lang;

    document.documentElement.dir = "ltr";
    document.documentElement.lang = "en";

    return () => {
      document.documentElement.dir = prevDir;
      document.documentElement.lang = prevLang;
    };
  }, []);

  useEffect(() => {
    if (
      requireFullscreenRef.current &&
      !document.fullscreenElement &&
      !isEndingRef.current
    ) {
      navigate("/placement/rules");
    }
  }, [navigate]);

  useEffect(() => {
    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          timerRef.current = null;
          endAndCleanup("Time is up. The test will now be submitted.");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!requireFullscreenRef.current) return undefined;

    const handleExitFullscreen = () => {
      if (!document.fullscreenElement) {
        if (isEndingRef.current) return;
        recordCheatingAttempt("You exited fullscreen. The test is now ending.");
        setTimeout(() => {
          dispatch(resetStudentAnswers());
          exitFullscreenAndNavigate();
        }, 4000);
      }
    };
    document.addEventListener("fullscreenchange", handleExitFullscreen);
    return () => document.removeEventListener("fullscreenchange", handleExitFullscreen);
  }, [dispatch, exitFullscreenAndNavigate, recordCheatingAttempt]);

  useEffect(() => {
    const cleanup = applyTestRestrictions((msg) => {
      if (isEndingRef.current) return;
      setModalMessage(msg);
      setOpenModal(true);
      recordCheatingAttempt(msg);
    });
    return () => cleanup();
  }, [recordCheatingAttempt]);

  const formatTime = (seconds) => {
    const min = Math.floor(seconds / 60);
    const sec = seconds % 60;
    return `${min.toString().padStart(2, "0")}:${sec.toString().padStart(2, "0")}`;
  };

  const parts = bundle?.parts || [];
  const activePart = parts[Math.min(tabIndex, Math.max(parts.length - 1, 0))];
  const isFirstPart = tabIndex <= 0;
  const isLastPart = parts.length > 0 && tabIndex >= parts.length - 1;

  const handleSelect = (q, idx) => {
    const text = (q?.options || [])[idx];
    dispatch(setMcqSelection(q.id, idx, String(text)));
  };

  const isBlankAtSentenceStart = (questionText = "") => {
    const text = String(questionText);
    const blankMatch = text.match(/_{2,}|-{2,}|\.{3,}|…/u);
    if (!blankMatch) return false;
    const beforeBlank = text.slice(0, blankMatch.index).trim();
    const beforeBlankWithoutNumber = beforeBlank.replace(/^\d+[).]?\s*/, "").trim();
    return beforeBlankWithoutNumber === "" || /[.!?]\s*$/.test(beforeBlankWithoutNumber);
  };

  const formatOptionForDisplay = (option, questionText) => {
    const value = String(option ?? "").trim();
    if (!value) return value;
    const shouldUppercase = isBlankAtSentenceStart(questionText);
    const optionLabelMatch = value.match(/^(\s*[a-dA-D][).:-]\s*)(.*)$/u);
    const prefix = optionLabelMatch ? optionLabelMatch[1] : "";
    const optionText = optionLabelMatch ? optionLabelMatch[2] : value;
    const firstLetterIndex = optionText.search(/\p{L}/u);
    if (firstLetterIndex < 0) return value;
    const firstLetter = optionText[firstLetterIndex];
    const formattedFirstLetter = shouldUppercase
      ? firstLetter.toLocaleUpperCase("de-DE")
      : firstLetter.toLocaleLowerCase("de-DE");
    return `${prefix}${optionText.slice(0, firstLetterIndex)}${formattedFirstLetter}${optionText.slice(firstLetterIndex + 1)}`;
  };

  const renderQuestion = (q, number, suppressContext = false) => {
    const selectedIdx = selected?.[q.id]?.index;
    const displayNumber = q?.orderNumber ?? number;
    const contextText = suppressContext
      ? ""
      : q.passageText || q.contextText || q.context || q.prompt || q.situationText || "";
    return (
      <Box key={q.id}>
        {contextText && (
          <Typography
            variant="h6"
            sx={{
              mb: 1,
              color: "#111",
              fontWeight: 800,
            }}
          >
            {displayNumber}. {contextText}
          </Typography>
        )}
        <Typography variant="h6" sx={{ mb: 1, color: "#111", fontWeight: 800 }}>
          {!contextText ? `${displayNumber}. ` : ""}{q.questionText}
        </Typography>
        <Stack spacing={1}>
          {(q.options || []).map((opt, idx) => {
            const isSelected = selectedIdx === idx;
            return (
              <Button
                key={idx}
                fullWidth
                onClick={() => handleSelect(q, idx)}
                variant={isSelected ? "contained" : "outlined"}
                sx={{
                  textAlign: "left",
                  justifyContent: "flex-start",
                  borderWidth: isSelected ? 2 : 1,
                  textTransform: "none",
                }}
              >
                {formatOptionForDisplay(opt, q.questionText)}
              </Button>
            );
          })}
        </Stack>
        <Divider sx={{ mt: 2 }} />
      </Box>
    );
  };

  const handleEndTest = () => setConfirmEndTest(true);
  const goToPreviousPart = () => setTabIndex((current) => Math.max(0, current - 1));
  const goToNextPart = () => setTabIndex((current) => Math.min(parts.length - 1, current + 1));
  const confirmEndTestYes = async () => {
    setConfirmEndTest(false);
    await endAndCleanup(t("placementSubmission.ended"));
  };

  return (
    <>
      <Meta
        title="Placement Test | ALGermanist"
        description="Complete the German placement test."
        keywords="placement test, German level test, ALGermanist"
      />
      <Box sx={{ p: 4, direction: "ltr" }} dir="ltr">
        <Typography variant="h4" gutterBottom>
          Placement Test
        </Typography>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Time Remaining: {formatTime(timeLeft)}
        </Typography>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          Target Level: {bundle?.targetLevel || "-"}
        </Typography>

        {bundleLoading && <CircularProgress />}
        {bundleError && <Alert severity="error">{String(bundleError)}</Alert>}

        {!bundleLoading && !bundleError && parts.length > 0 && (
          <Tabs value={Math.min(tabIndex, parts.length - 1)} onChange={(_, v) => setTabIndex(v)} sx={{ mb: 2 }}>
            {parts.map((part) => (
              <Tab key={part.teil} label={`Teil ${part.teil} - ${part.label}`} />
            ))}
          </Tabs>
        )}

        <Card>
          <CardContent>
            {!bundleLoading && !bundleError && parts.length === 0 && (
              <Alert severity="warning">No active placement test parts are configured for this level.</Alert>
            )}

            {activePart && (
              <Box>
                <Stack spacing={3}>
                  {(activePart.questions || []).map((q, qIdx) =>
                    renderQuestion(q, qIdx + 1)
                  )}

                  {(activePart.passages || []).map((passage, passageIdx) => (
                    <Box
                      key={passage.id || passage.passageKey || passageIdx}
                      sx={{ p: 2, bgcolor: "#F6F8F8", borderRadius: 2, border: "1px solid #E5E7EB" }}
                    >
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 800 }}>
                        {passage.title}
                      </Typography>
                      {(passage.content || "").split("\n").map((line, idx) => (
                        <Typography
                          key={idx}
                          variant="h6"
                          paragraph
                          sx={{ mb: 1, color: "#111", fontWeight: 800 }}
                        >
                          {line}
                        </Typography>
                      ))}
                      <Stack spacing={3} sx={{ mt: 2 }}>
                        {(passage.questions || []).map((q, qIdx) =>
                          renderQuestion(q, qIdx + 1, true)
                        )}
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}
          </CardContent>
        </Card>

        <Box sx={{ mt: 3, display: "flex", justifyContent: "center", gap: 2, flexWrap: "wrap" }}>
          {!isFirstPart && (
            <Button variant="outlined" onClick={goToPreviousPart}>
              Back
            </Button>
          )}
          {!isLastPart && parts.length > 0 && (
            <Button variant="contained" onClick={goToNextPart}>
              Next Teil
            </Button>
          )}
          {isLastPart && parts.length > 0 && (
            <Button
              variant="contained"
              onClick={handleEndTest}
              sx={{
                bgcolor: "#ffc400",
                color: "#111",
                fontWeight: 800,
                fontSize: "1rem",
                px: 3.5,
                py: 1.25,
                boxShadow: "0 5px 14px rgba(255, 196, 0, .32)",
                "&:hover": { bgcolor: "#e6b000" },
              }}
            >
              {t("placementTestControls.endAndShowResult")}
            </Button>
          )}
        </Box>

        <Dialog open={openModal} onClose={() => !guestCompleted && !studentCompleted && setOpenModal(false)} PaperProps={{ dir: isArabic ? "rtl" : "ltr" }}>
          <DialogTitle>{t("placementSubmission.notice")}</DialogTitle>
          <DialogContent><Typography whiteSpace="pre-line" textAlign={isArabic ? "right" : "left"}>{modalMessage}</Typography></DialogContent>
          <DialogActions>
            {guestCompleted ? (
              <>
                <Button onClick={() => exitFullscreenAndNavigate("/")}>{t("placementSubmission.later")}</Button>
                <Button
                  variant="contained"
                  onClick={() => {
                    const email = localStorage.getItem("placementGuestEmail") || "";
                    exitFullscreenAndNavigate(`/signup/complete-placement?email=${encodeURIComponent(email)}`);
                  }}
                >
                  {t("placementTestControls.createAccountAndKeepResult")}
                </Button>
              </>
            ) : studentCompleted ? (
              <Button variant="contained" onClick={() => exitFullscreenAndNavigate("/dashboard?tab=placementTestResult")}>
                {t("placementTestControls.openDashboard")}
              </Button>
            ) : (
              <Button onClick={() => setOpenModal(false)}>{t("common.ok", { defaultValue: "OK" })}</Button>
            )}
          </DialogActions>
        </Dialog>

        <Dialog open={confirmEndTest} onClose={() => setConfirmEndTest(false)} PaperProps={{ dir: isArabic ? "rtl" : "ltr" }}>
          <DialogTitle>{t("placementTestControls.confirmEndTitle")}</DialogTitle>
          <DialogContent><Typography>{t("placementTestControls.confirmEndMessage")}</Typography></DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmEndTest(false)}>{t("placementTestControls.continueTest")}</Button>
            <Button
              onClick={confirmEndTestYes}
              variant="contained"
              sx={{ bgcolor: "#ffc400", color: "#111", fontWeight: 800, "&:hover": { bgcolor: "#e6b000" } }}
            >
              {t("placementTestControls.confirmEnd")}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};

export default PlacementTestMain;
